import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@ohif/ui';
import { useLocation } from 'react-router-dom';
import '../../custom/styled/pages/dashboard.scss';
import { formatTimeInAMPM } from '../Utils/utils';
import { capitalizeFirstLetterOfEachWord } from '../Utils/utils';
import { AppDispatch, RootState } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getCommonData } from '../rtk-apis/common/getCommonData';
import { changeReportStatus } from '../rtk-apis/reports/changeReportStatus';
import { downloadPDF } from '../rtk-apis/reports/downloadPdf';
import { downloadRtf } from '../rtk-apis/reports/downloadRtf';
import { sendReportNotification } from '../rtk-apis/reports/sendReportNotification';
import { Loader, Success, Failed } from '../components';
import { clearChangeReportStatus } from '../store/slices/reports/changeReportStatus';
import { clearDownloadPdfState } from '../store/slices/reports/downloadPdf';
import BackButton from './BackButton';
import QuickDistributePopup from './QuickDistributePopup';
import ReportPdfViewerDialog from './ReportPdfViewerDialog';
import { fetchReportPdf, isMobileDevice } from '../Utils/fetchReportPdf';
import { captureDiagramHtml } from '../Utils/captureDiagramHtml';
import { servicesManager } from '../../../platform/app/src/App';
import { getRole, getUserId, getName } from '../Utils/authUtils';
import { setReportStatusCell } from '../Utils/reportStatusCell';
import { canChangeReportStatus, isAssignedDoctor, canUnfinalizeReport, isSonographerOrTechnicianRole } from '../Utils/reportPermissions';

const ReportCard = ({
  ReportData,
  setHitGetReportApi,
  flushReportEdits,
  selectedTab,
  updateTemplateContent,
  isTemplateValid,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { uiNotificationService } = servicesManager.services;
  const [ReportStatusOptions, setReportStatusOptions] = useState([]);
  const [SelectedReportStatus, setSelectedReportStatus] = useState('');
  const [InitialReportStatus, setInitialReportStatus] = useState('');
  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isDownloadInitiated, setIsDownloadInitiated] = useState(false);
  const [quickDistributeOpen, setQuickDistributeOpen] = useState(false);
  const [isNotifModalOpen, setIsNotifModalOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [isSendingNotif, setIsSendingNotif] = useState(false);
  const [notifResult, setNotifResult] = useState<{ msg: string; ok: boolean } | null>(null);
  const role = getRole();

  const { result: commonData } = useSelector(
    (state: RootState) => state.getCommonDataSliceResponse
  );
  const { status, data, msg, loading } = useSelector(
    (state: RootState) => state.changeReportStatusResponse
  );
  const {
    result: downloadData,
    status: downloadStatus,
    msg: downloadMsg,
    loading: downloadLoading,
  } = useSelector((state: RootState) => state.downloadPdfResponse);


  useEffect(() => {
    if (ReportData?.data?.status) {
      setInitialReportStatus(ReportData?.data?.status);
    }
  }, [ReportData?.data?.status]);

  useEffect(() => {
    if (commonData?.reportStatus?.length > 0) {
      setReportStatusOptions(commonData?.reportStatus);
    }
  }, [commonData]);

  const [isOpen, setIsOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDropdownDisabled = () => {
    const currentStatus = ReportData?.data?.status;
    if (!canChangeReportStatus(role, currentStatus)) return true;
    const userId = getUserId();
    const userName = getName();
    // Doctor must be assigned to this report to change status (sonographers not restricted)
    if (!isAssignedDoctor(role, ReportData?.data?.reportAssignedToId, userId, ReportData?.data?.reportAssignedTo, userName)) return true;
    // If finalized, only the doctor who finalized can unfinalize (sonographers already blocked above)
    if (currentStatus === 'Finalized') {
      return !canUnfinalizeReport(role, currentStatus, ReportData?.data?.finalizedById, userId);
    }
    return false;
  };

  const toggleDropdown = () => {
    if (!isDropdownDisabled()) {
      setIsOpen(!isOpen);
    }
  };

  useEffect(() => {
    if (SelectedReportStatus && ReportData?.data?._id) {
      if (!setReportStatusCell(updateTemplateContent, SelectedReportStatus)) {
        console.warn('reportStatus object not found in templateHTML');
      }
      const payload = {
        reportId: ReportData?.data?._id,
        reportStatus: SelectedReportStatus,
        templateHTML: updateTemplateContent,
      };
      dispatch(changeReportStatus(payload));
    }
  }, [SelectedReportStatus]);

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        setHitGetReportApi(true);
        if (SelectedReportStatus === 'Finalized') {
          setQuickDistributeOpen(true);
        }
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };
    if (status === 'SUCCESS') {
      handleSuccess(msg);
    } else if (status === 'FAILED') {
      handleFailure(msg);
    }

    return () => {
      dispatch(clearChangeReportStatus());
    };
  }, [status]);

  useEffect(() => {
    const handleDownloadSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        setIsDownloadInitiated(false);
        dispatch(clearDownloadPdfState());
      }, 1500);
    };

    const handleDownloadFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
        setIsDownloadInitiated(false);
        dispatch(clearDownloadPdfState());
      }, 1500);
    };

    if (isDownloadInitiated) {
      if (downloadStatus === 'SUCCESS') {
        handleDownloadSuccess(downloadMsg || 'PDF downloaded successfully');
      } else if (downloadStatus === 'ERROR') {
        handleDownloadFailure(downloadMsg || 'Failed to download PDF');
      }
    }
  }, [downloadStatus, downloadMsg, isDownloadInitiated]);

  const handleDownloadPDF = () => {
    if (ReportData?.data?._id && isTemplateValid) {
      setIsDownloadInitiated(true);
      let reportDiagrams;
      if (ReportData?.data?.reportType == 'Stress Echo') {
        reportDiagrams = generateReportDiagrams();
      }
      const fullName = ReportData?.data?.name || 'report';
      const firstName = fullName.split(' ')[0].replace(/[^a-zA-Z0-9]/g, '') || 'report';
      const payload = {
        _id: ReportData?.data?._id,
        firstName,
        reportDiagramHtml: reportDiagrams || '',
      };
      dispatch(downloadPDF(payload));
    } else {
      console.error('Report ID is not available or template is invalid');
    }
  };

  const handleDownloadRtf = async () => {
    const id = ReportData?.data?._id;
    if (!id) return;
    const fullName = ReportData?.data?.name || 'report';
    const firstName = fullName.split(' ')[0].replace(/[^a-zA-Z0-9]/g, '') || 'report';
    const res: any = await dispatch(downloadRtf({ _id: id, firstName }));
    const failed = Boolean(res?.error);
    setDisplayMessage(failed ? res?.payload || 'Failed to download RTF' : 'RTF downloaded successfully');
    if (failed) {
      setIsFailedModalOpen(true);
    } else {
      setIsSuccessModalOpen(true);
    }
    setTimeout(() => {
      setIsFailedModalOpen(false);
      setIsSuccessModalOpen(false);
      setDisplayMessage('');
    }, 1500);
  };

  const handleDownloadButtonHover = useCallback(() => {
    console.log('Hover event triggered on Download PDF button');
    if (!isTemplateValid) {
      uiNotificationService.show({
        title: 'Download PDF',
        message: 'Report template is not updated',
        type: 'error',
      });
    }
  }, [isTemplateValid, uiNotificationService]);

  const generateReportDiagrams = () =>
    captureDiagramHtml(document.querySelector('.report-diagrams'));

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
    },
    [pdfViewerUrl]
  );

  const showViewError = (message: string) => {
    setDisplayMessage(message);
    setIsFailedModalOpen(true);
    setTimeout(() => {
      setIsFailedModalOpen(false);
      setDisplayMessage('');
    }, 1500);
  };

  // Shows the same server-rendered PDF that Download PDF saves. Mobile browsers often
  // can't show a PDF inside the page, so they get a new tab instead of the dialog.
  const handleViewPDF = async () => {
    const reportId = ReportData?.data?._id;
    if (!reportId || !isTemplateValid) return;

    // Opened before any await: a tab opened after one is blocked as a popup.
    const mobileTab = isMobileDevice() ? window.open('', '_blank') : null;
    if (!mobileTab) {
      setPdfViewerUrl(null);
      setPdfViewerOpen(true);
    }

    const saved = await flushReportEdits();
    if (!saved) {
      mobileTab?.close();
      setPdfViewerOpen(false);
      showViewError('Could not save your latest changes. Please try again.');
      return;
    }

    const diagramHtml =
      ReportData?.data?.reportType == 'Stress Echo' ? generateReportDiagrams() : '';
    const result = await fetchReportPdf(reportId, diagramHtml);
    if (result.ok === false) {
      mobileTab?.close();
      setPdfViewerOpen(false);
      showViewError(result.error);
      return;
    }

    const url = URL.createObjectURL(result.blob);
    if (mobileTab) {
      mobileTab.location.href = url;
      // The tab keeps its own copy once loaded.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      setPdfViewerUrl(url);
    }
  };

  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    setPdfViewerUrl(null);
  };

  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      const metaViewport = document.querySelector('meta[name="viewport"]');
      if (metaViewport) {
        if (
          location.pathname.startsWith('/viewer') ||
          location.pathname === '/' ||
          location.pathname.includes('/patientReports')
        ) {
          metaViewport.setAttribute('content', 'width=1024');
        } else {
          metaViewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=0'
          );
        }
      }
    }, 100);
  }, [location.pathname]);

  return (
    <div className="mt-2 p-2">
      <BackButton />
      <div className="reportCard">
        <div className="reportCard__head">
          <div className="profile">
            <Icon name="username-icon" />
            {`${capitalizeFirstLetterOfEachWord(ReportData?.data?.name)}`}
          </div>
          <div className="btn__group">
            <button
              className="btn btn__white btn__outlined"
              onClick={handleViewPDF}
              disabled={selectedTab !== 1 || !isTemplateValid}
              title={!isTemplateValid ? 'Report template is not updated' : ''}
              style={{
                cursor: selectedTab !== 1 || !isTemplateValid ? 'not-allowed' : 'pointer',
                opacity: selectedTab !== 1 || !isTemplateValid ? 0.6 : 1,
              }}
            >
              View PDF
            </button>
            <div onMouseEnter={handleDownloadButtonHover}>
              <button
                className="btn btn__white btn__outlined"
                onClick={handleDownloadPDF}
                disabled={selectedTab !== 1 || !isTemplateValid}
                title={!isTemplateValid ? 'Report template is not updated' : ''}
                style={{
                  cursor: selectedTab !== 1 || !isTemplateValid ? 'not-allowed' : 'pointer',
                  opacity: selectedTab !== 1 || !isTemplateValid ? 0.6 : 1,
                }}
              >
                Download PDF
              </button>
            </div>
            <button
              className="btn btn__white btn__outlined"
              onClick={handleDownloadRtf}
              disabled={selectedTab !== 1 || !ReportData?.data?._id}
              style={{
                cursor: selectedTab !== 1 || !ReportData?.data?._id ? 'not-allowed' : 'pointer',
                opacity: selectedTab !== 1 || !ReportData?.data?._id ? 0.6 : 1,
              }}
            >
              Download RTF
            </button>
            <div className="customSelect" ref={statusDropdownRef}>
              <button
                className="customSelect__caption"
                onClick={toggleDropdown}
                role="button"
                tabIndex={0}
                disabled={isDropdownDisabled()}
                style={{
                  cursor: isDropdownDisabled() ? 'not-allowed' : 'pointer',
                  opacity: isDropdownDisabled() ? 0.6 : 1,
                }}
              >
                Status: {SelectedReportStatus ? SelectedReportStatus : InitialReportStatus}
                <Icon name="arrow-down" />
              </button>
              {isOpen && (
                <ul className="customSelect__dropdown">
                  {ReportStatusOptions?.map((status, index) => {
                    if (ReportData?.data?.status === status) return null;
                    if (isSonographerOrTechnicianRole(role) && status === 'Finalized') return null;
                    return (
                      <li
                        className="item"
                        key={index}
                        value={status}
                        onClick={() => {
                          setSelectedReportStatus(status);
                          setIsOpen(false);
                        }}
                      >
                        {status}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {ReportData?.data?._id && ReportData?.data?.reportAssignedToId && (
              <button
                className="btn btn__white btn__outlined"
                onClick={() => { setNotifResult(null); setIsNotifModalOpen(true); }}
              >
                Send Notification
              </button>
            )}
          </div>
        </div>
        <ul className="reportCard__info">
          <li>
            <button className="btn btn__success">Type: {ReportData?.data?.reportName}</button>
          </li>
          <li>DOB: {ReportData?.data?.dob}</li>
          <li>Sex: {ReportData?.data?.sex}</li>
          <li>Age: {ReportData?.data?.age}</li>
          <li>Date: {ReportData?.data?.date ? ReportData?.data?.date : '--'}</li>
          <li>Time: {ReportData?.data?.time ? formatTimeInAMPM(ReportData?.data?.time) : '--'}</li>
        </ul>
      </div>
      <Loader isLoading={loading || downloadLoading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
      <ReportPdfViewerDialog
        open={pdfViewerOpen}
        onClose={closePdfViewer}
        title={`${capitalizeFirstLetterOfEachWord(ReportData?.data?.name || '')} — ${ReportData?.data?.reportName || 'Report'}`}
        pdfUrl={pdfViewerUrl}
      />
      {quickDistributeOpen && ReportData?.data?.studyInstanceUID && (
        <QuickDistributePopup
          studyInstanceUID={ReportData.data.studyInstanceUID}
          patientInfo={{
            name: ReportData.data.name,
            patientName: ReportData.data.name,
            dob: ReportData.data.dob,
            reportName: ReportData.data.reportName,
            date: ReportData.data.date,
            time: ReportData.data.time,
            patientId: ReportData.data.patientId,
          }}
          onClose={() => setQuickDistributeOpen(false)}
        />
      )}

      {isNotifModalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setIsNotifModalOpen(false)}
        >
          <div
            style={{
              background: '#1a2336', borderRadius: 10, padding: '28px 28px 24px',
              width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#e2e8f0' }}>Send Notification</h3>
            <textarea
              rows={4}
              placeholder="Type your message..."
              value={notifMessage}
              onChange={e => setNotifMessage(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f1824', border: '1px solid #2d3f5a',
                borderRadius: 6, color: '#e2e8f0', padding: '10px 12px',
                fontSize: 14, resize: 'vertical',
              }}
            />
            {notifResult && (
              <p style={{ marginTop: 8, fontSize: 13, color: notifResult.ok ? '#4ade80' : '#f87171' }}>
                {notifResult.msg}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn__secondary" onClick={() => setIsNotifModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn__primary"
                disabled={!notifMessage.trim() || isSendingNotif}
                onClick={async () => {
                  if (!ReportData?.data?._id || !notifMessage.trim()) return;
                  setIsSendingNotif(true);
                  setNotifResult(null);
                  const result = await dispatch(sendReportNotification({ reportId: ReportData.data._id, message: notifMessage.trim() }));
                  setIsSendingNotif(false);
                  if (sendReportNotification.fulfilled.match(result)) {
                    setNotifMessage('');
                    setNotifResult({ msg: 'Notification sent.', ok: true });
                    setTimeout(() => setIsNotifModalOpen(false), 1200);
                  } else {
                    setNotifResult({ msg: (result.payload as string) || 'Failed to send.', ok: false });
                  }
                }}
              >
                {isSendingNotif ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportCard;
